import React from 'react';
import dayjs from 'dayjs';
import { Box, Tooltip, Typography, useTheme, alpha, SxProps, Theme } from '@mui/material';
import { useLiveTat } from 'hooks/useLiveTat';
import { ModernTATIcon } from 'utils/TaskManagementIcon/taskmanagemeneticon';
import { formatTatDuration, parseDateClean } from 'utils/tatUtils';

export interface TatChipProps {
    startedOn?: string | null;
    completedOn?: string | null;
    elapsedMinutes?: string | number | null;
    sx?: SxProps<Theme>;
    variant?: 'chip' | 'badge' | 'text';
    size?: 'small' | 'medium';
    daysOnly?: boolean;
    showIcon?: boolean;
    color?: string;
}

/**
 * Reusable TAT Chip component.
 * 
 * Features:
 * - Computes and renders live TAT using useLiveTat hook or uses static elapsedMinutes prop.
 * - Tooltip: "Elapsed Time" or full TAT string when daysOnly is true.
 * - Supports daysOnly mode (e.g. displays "2d" while tooltip shows "Elapsed Time: 2d, 04:30h").
 * - Hides completely when neither startedOn nor elapsedMinutes is present or invalid.
 */
export const TatChip: React.FC<TatChipProps> = React.memo(({
    startedOn,
    completedOn,
    elapsedMinutes,
    sx,
    variant = 'chip',
    size = 'small',
    daysOnly = false,
    showIcon = true,
    color
}) => {
    const rawElapsed = elapsedMinutes !== undefined && elapsedMinutes !== null ? elapsedMinutes : null;
    const formattedElapsed = typeof rawElapsed === 'number' ? formatTatDuration(rawElapsed) : rawElapsed;

    // Fast exit if no data is provided
    if (!startedOn && (!formattedElapsed || formattedElapsed === '--')) {
        return null;
    }

    const theme = useTheme();
    const liveTat = useLiveTat(startedOn, completedOn);

    // Use backend-provided formattedElapsed/elapsedMinutes directly, or fallback to liveTat calculation
    const displayTat = (formattedElapsed && formattedElapsed !== '--') ? formattedElapsed : (liveTat || null);

    // Rule: Hide when no valid TAT display
    if (!displayTat || displayTat === '--') {
        return null;
    }

    // Extract days for daysOnly mode (e.g. "2d, 04:30h" -> "2d", "<1d" / "0d" for hours/minutes)
    let displayText = displayTat;
    if (daysOnly) {
        const daysMatch = displayTat.match(/^(\d+d)/);
        if (daysMatch) {
            displayText = daysMatch[1];
        } else if (displayTat === '0m' || displayTat.includes('m') || displayTat.includes('h')) {
            displayText = '0d';
        }
    }

    // Check if start time is in the future for In-Progress tasks
    const parsedStart = parseDateClean(startedOn);
    const parsedCompleted = parseDateClean(completedOn);
    const isFutureStart = Boolean(
        parsedStart && !parsedCompleted && parsedStart.isValid() && parsedStart.isAfter(dayjs())
    );
    const startTimeFormatted = isFutureStart && parsedStart
        ? parsedStart.isSame(dayjs(), 'day')
            ? parsedStart.format('hh:mm A')
            : parsedStart.format('MM/DD/YYYY hh:mm A')
        : '';

    const tooltipTitle = isFutureStart
        ? `Elapsed Time: ${displayTat} (Starts at ${startTimeFormatted})`
        : daysOnly
            ? `Elapsed Time: ${displayTat}`
            : 'Elapsed Time';

    if (variant === 'text') {
        return (
            <Tooltip title={tooltipTitle} arrow placement="top">
                <Box
                    component="span"
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        color: color || 'text.secondary',
                        fontSize: '13px',
                        fontWeight: 400,
                        lineHeight: 1,
                        cursor: 'inherit',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        ...sx
                    }}
                >
                    {showIcon && <ModernTATIcon style={{ width: 13, height: 13, color: color || theme.palette.info.main, flexShrink: 0 }} />}
                    <span style={{ whiteSpace: 'nowrap' }}>{displayText}</span>
                </Box>
            </Tooltip>
        );
    }

    return (
        <Tooltip title={tooltipTitle} arrow placement="top">
            <Box
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.6,
                    px: size === 'small' ? 1.5 : 1,
                    py: size === 'small' ? 0.5 : 0.5,
                    borderRadius: '6px',
                    bgcolor: alpha(theme.palette.info.main, 0.08),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                    color: 'info.dark',
                    fontSize: '13px',
                    fontWeight: 500,
                    cursor: 'inherit',
                    whiteSpace: 'nowrap',
                    ...sx
                }}
            >
                {showIcon && <ModernTATIcon style={{ width: 12, height: 12, color: color || theme.palette.info.main }} />}
                <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit', lineHeight: 1 }}>
                    {displayText}
                </Typography>
            </Box>
        </Tooltip>
    );
});

export default TatChip;


